import { useState } from "react";
import { Link } from "react-router-dom";
import { FolderPlus, Plus, UploadCloud } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { FolderNameDialog } from "./folder-name-dialog";

type Props = {
  uploadHref: string;
  /** Async — receives the trimmed name and creates the folder via the mutation. */
  onCreateFolder: (name: string) => void | Promise<void>;
};

/**
 * "Upload Asset" and "Upload Folder" both land on the REAL upload page
 * (src/pages/upload.tsx) — "Upload Folder" adds &mode=folder, which flips
 * the file input to accept a whole OS folder (webkitdirectory) instead of
 * individual files. Bulk-folder ingestion into a real backend folder tree
 * is still frontend-only (2026-07-14) — the uploaded files land in the
 * current project/folder scope as flat assets. "New Folder" IS backed by
 * migration 0012.
 */
export function UploadMenu({ uploadHref, onCreateFolder }: Props) {
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const folderUploadHref = `${uploadHref}&mode=folder`;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" aria-label="Add">
            <Plus className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem asChild>
            <Link to={uploadHref}>
              <UploadCloud />
              Upload Asset
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to={folderUploadHref}>
              <UploadCloud />
              Upload Folder
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setFolderDialogOpen(true)}>
            <FolderPlus />
            New Folder
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <FolderNameDialog
        mode="create"
        open={folderDialogOpen}
        onOpenChange={setFolderDialogOpen}
        onSubmit={onCreateFolder}
      />
    </>
  );
}
